import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, ExternalLink, Expand } from "lucide-react";
import SocialShare from "@/components/SocialShare";
import Lightbox from "@/components/Lightbox";
import BatlleTimeline from "@/components/BatlleTimeline";
import PageSEO from "@/components/PageSEO";
import { useLang } from "@/i18n/LanguageContext";
import { SITE_CONTENT_EN, SITE_POETRY_EN, SITE_CURATORS_EN, mergeSiteContent } from "@/i18n/siteContentEn";
import PosterComposite from "@/components/PosterComposite";

// Rich content per site slug — extend as programmes get confirmed.
const SITE_CONTENT = {
  "saint-ferreol": {
    tagline: "Une carte blanche à Michel Batlle",
    hero: {
      title: "Michel Batlle",
      subtitle: "80 ans de création",
      body: [
        "Michel Batlle a 80 ans. Il vit à Saint-Ferréol. Il y a 40 ans, il rendait déjà hommage à Riquet avec une sculpture à son effigie, aujourd'hui présente sur le bassin de Saint-Ferréol.",
        "Sculpteur, peintre et penseur, il travaille depuis six décennies aux confins du langage plastique : figures chamaniques, cosmogonies de papier, philosophes de l'âpre histoire. Son œuvre habite les espaces intérieurs comme les grands paysages avec une présence saisissante.",
        "Ses œuvres ont été exposées dans des institutions prestigieuses — le Château de Laréole (Conseil départemental de la Haute-Garonne), la Fondation Salinger, l'Abbaye de Valmagne, le Musée Soulages à Rodez, le Parc de la Préhistoire de Tarascon-sur-Ariège — et dans de nombreuses villes en France et à l'étranger.",
      ],
    },
    projet: {
      title: "EXPLOSITION au Bassin de Saint-Ferréol",
      lead: "Les Arteurs donnent carte blanche à Michel Batlle pour le bassin de Saint-Ferréol, premier grand barrage d'Europe, chef-d'œuvre hydraulique de Riquet, inscrit à l'UNESCO.",
      stats: [
        { n: "30", u: "sculptures monumentales", d: "de 3 à 5 mètres" },
        { n: "29", u: "artistes invités",         d: "par Michel Batlle" },
        { n: "40", u: "ans",                       d: "après son premier hommage à Riquet" },
      ],
      quote: "« 30 sculptures de 3 à 5 mètres prendront place sur le bassin de Saint-Ferréol. Un hommage monumental à l'homme et à l'œuvre, 40 ans après. »",
      quoteAuthor: "— Les Arteurs",
    },
    works: [
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/3e0h2swi_Gemini_Generated_Image_fv65ngfv65ngfv65.png", caption: "Sculptures monumentales de Michel Batlle sur la digue · Bassin de Saint-Ferréol", isSimulation: true },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/irevxper_dans%20la%20cascade.jpg", caption: "Sculpture de Michel Batlle dans le bassin de la cascade · Saint-Ferréol" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/hla108on_sculpturre%20de%20Batlle%20devant%20la%20cascade%20de%20Saint%20Ferr%C3%A9ol.jpeg", caption: "Sculpture de Michel Batlle devant la cascade de Saint-Ferréol", isSimulation: true },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rie52kbp_1%20Chamane%20Abbaye%20de%20Valmagne.webp", caption: "Chamane · Abbaye de Valmagne" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/03namgzi_OIP.webp", caption: "Un artiste de plein vent · Château de Laréole" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/5mf2qam4_Parc%20Pr%C3%A9histoire%20%20Tarascon.jpg", caption: "Parc de la Préhistoire · Tarascon-sur-Ariège" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/0crsq1pp_Fondation%20Salinger%20chamans.webp", caption: "Chamanes · Fondation Salinger · Été 2017" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/uignjhxq_image.jpg", caption: "Chamane · exposition en abbaye" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rfh0atv5_Philosophes%20de%20l%27%C3%A2pre%20histoire%20Rodez.webp", caption: "Philosophes de l'âpre histoire · Musée Soulages, Rodez" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/3fagswia_image%20%285%29.jpg", caption: "Sculpture embarquée · péniche Sanctanox, Canal du Midi" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/6ey8u3ea_El3D_m9BGZUbeo9iGQN0Fjl72eJkfbmt4t8yenImKBVvK0kTmF0xjctABnaLJIm9.jpg", caption: "Sculptures monumentales · Château de Laréole" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/7wiu8mfh_image%20%287%29.jpg", caption: "L'œuvre au contact du public · sculpture partagée" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/0kodkvp6_image%20%288%29.jpg", caption: "Installation « Philosophes » · dessins sur papier, exposition" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/hzblmbwz_image%20%2810%29.jpg", caption: "Nef de l'Abbaye de Valmagne · vue d'ensemble" },
    ],
    invitedArtists: [
      { slug: "olga-yameogo", name: "Olga Yaméogo", country: "Burkina Faso", image: "https://les-arteurs.org/wp-content/uploads/2024/06/Olga-Yameogo-2.jpg" },
      { slug: "kossi-homawoo", name: "Kossi Homawoo", country: "Togo", image: "https://les-arteurs.org/wp-content/uploads/2024/06/Kossi-Homawoo-Vanites-I.jpg" },
      { slug: "daflag", name: "Daflag", country: "Togo", image: "https://les-arteurs.org/wp-content/uploads/2024/06/Daflag-127218424_296175474989165_3211459179362084841_n.jpg" },
      { slug: "jin-bo", name: "Jin Bo", country: "France · Chine", image: "https://les-arteurs.org/wp-content/uploads/2024/07/jin.jpg" },
      { name: "Philippe Assalit", sub: "philippeassalit.com", url: "https://philippeassalit.com" },
      { name: "Pierre Segoh", sub: "Togo" },
      { name: "Rolino Gaspari", sub: "Plasticien · Occitanie" },
      { name: "Philippe Lagaillarde", sub: "Sculptures · Installations" },
      { name: "Gérard Marty", sub: "gerardmarty.com", url: "https://gerardmarty.com" },
      { name: "Valencak", sub: "Peintre" },
      { name: "Gilou Estève", sub: "Artiste naïve · Revel" },
      { name: "Patricorel", sub: "Peintre · Galerie en ligne" },
      { name: "Renaud Agbemadon", sub: "Artiste · Designer · Togo / France" },
      { name: "Ibrahim Chahamata", sub: "Peintre · Plasticien" },
      { name: "Jean-Luc Fau", sub: "Chef & peintre · Rodez" },
      { name: "Papillion Delbruel", sub: "Art brut · Singulier" },
      { name: "Valérie Kanteova", sub: "Plasticienne · Photographe" },
      { name: "Aziza El Aabidi", sub: "Peintre · Plasticienne" },
      { name: "Igor Boyé", sub: "Peintre · Sculpteur · Occitanie" },
      { name: "Laurent Esquerré", sub: "Sculptures & céramiques", url: "https://laurentesquerre.com" },
      { name: "Masayuki Kai", sub: "Sculpteur" },
      { name: "Yachiyo Sako", sub: "Plasticienne" },
      { name: "Obey Fal", sub: "Peintre · Plasticien" },
    ],
    invited: {
      title: "Les 29 artistes invités",
      body: "Michel Batlle choisit lui-même les 29 artistes qui l'accompagneront. Leurs œuvres seront accrochées aux arbres autour du bassin de Saint-Ferréol, répondant à la même thématique.",
      count_announced: 23,
      total: 29,
      note: "Les artistes sont annoncés progressivement par Michel Batlle. Cette page est mise à jour au fil des confirmations.",
    },
    closing: {
      hashtag: "Canal'Art 2026",
      title: "Même lieu, même thème. Un hommage monumental.",
      body: "Il y a 40 ans, Michel Batlle sculptait l'hommage à Riquet au bord de ce bassin. Aujourd'hui, il revient avec 30 œuvres monumentales et 29 artistes invités. C'est le projet juste.",
      cta: "Découvrir le dossier EXPLOSITION",
    },
  },

  // ────────────────────────────────────────────────────────────────
  "castelnau": {
    heroKind: "site",
    tagline: "Le foyer des Arteurs · Chapitre inaugural",
    posterComposite: {
      bgImage: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/ck07l1zz_Moi%20presentant%20l%27expo.webp",
      eyebrow: "CASTELNAU",
      artist: "Michel Batlle",
      artistCount: "et 29 artistes invités",
      festival: "LE FIL DU MIDI",
      edition: "CANAL'ART 2026",
      location: "CASTELNAU D'ESTRÉTEFONDS",
      homage: "Le cœur du FILM · hors zone classée UNESCO",
      dates: "SEPT 2026 — AOÛT 2027",
      logos: [
        { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rf3uctbk_Logo_Castelnau_reseaux_rvb-1024x518.webp", alt: "Ville de Castelnau d'Estrétefonds" },
        { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/pm6o2yg0_ARTEURS_Noir.webp", alt: "Les Arteurs" },
      ],
    },
    hero: {
      heroLabel: "§ Le lieu",
      title: "Le lieu de naissance",
      subtitle: "40 Grande Rue · Siège du collectif",
      body: [
        "Castelnau d'Estrétefonds est le berceau du Fil du Midi. C'est ici, dans la Grande Rue, que Les Arteurs se réunissent depuis des années pour imaginer, organiser et faire vivre la scène artistique locale — puis, aujourd'hui, ce grand parcours de 750 km.",
        "Le collectif y a organisé de nombreuses expositions — dont l'édition Octobre Rose en partenariat avec la mairie — et y accueille régulièrement artistes, mécènes et publics. La commune est le premier partenaire institutionnel du projet.",
        "En 2026, Castelnau devient le chapitre inaugural du parcours : le site d'expérimentation où se prépare l'ADN du Fil avant qu'il ne se déploie de l'Atlantique à la Méditerranée.",
      ],
    },
    projet: {
      title: "Chapitre 01 · L'atelier du Fil",
      lead: "Castelnau d'Estrétefonds ouvre le Fil. Ateliers, résidences, vernissages préparatoires : c'est ici que les 6 sites s'inventent ensemble avant de se déployer sur le canal. Hors zone classée UNESCO, les expositions peuvent y durer autant qu'on le souhaite.",
      stats: [
        { n: "01", u: "chapitre inaugural", d: "point d'ancrage du projet" },
        { n: "30", u: "artistes",           d: "invités par Les Arteurs" },
        { n: "12", u: "mois d'exposition",  d: "sept 2026 → août 2027" },
      ],
      quote: "« Avant que le Fil ne rejoigne la Méditerranée, il commence dans une salle communale, un atelier, une conversation partagée. »",
      quoteAuthor: "— Les Arteurs",
    },
    worksLabel: "§ Le lieu · Regards",
    works: [
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rkaa9hod_estr-tefonds-1.webp", caption: "Castelnau d'Estrétefonds · vue du bourg" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/ck07l1zz_Moi%20presentant%20l%27expo.webp", caption: "Présentation de l'exposition · salle communale" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/1w5658ri_Moi%2C%20La%20maire%20et%20l%27adjointe%20%C3%A0%20la%20culture%20de%20Castelnau.webp", caption: "Avec Mᵐᵉ la Maire et l'adjointe à la Culture" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/l9m6m6sa_affiche%20Octobre%20Rose.webp", caption: "Octobre Rose · édition Les Arteurs × Ville de Castelnau d'Estrétefonds" },
    ],
    invitedArtists: [
      { slug: "michel-batlle", name: "Michel Batlle", country: "France", role: "Directeur artistique · invitant" },
      { slug: "olga-yameogo", name: "Olga Yaméogo", country: "Burkina Faso" },
      { slug: "jin-bo", name: "Jin Bo", country: "Chine" },
      { slug: "ras-sankara", name: "RAS SANKARA", country: "Burkina Faso" },
      { slug: "pierre-sego", name: "Pierre Sego", country: "France" },
      { slug: "flipo", name: "Flipo", country: "France" },
      { slug: "yao-metsoko", name: "Yao Metsoko", country: "Togo" },
    ],
    invited: {
      title: "Michel Batlle invite",
      body: "Michel Batlle, directeur artistique du Fil, invite personnellement 6 artistes de renom qui composeront le noyau du chapitre castelnaudéen. Le reste de la programmation (30 artistes au total) est co-curaté avec Les Arteurs et sera annoncé au fil des confirmations.",
      count_announced: 7,
      total: 30,
      note: "Vous êtes une commune, une école, une association ? Rejoignez ce chapitre, cœur du FILM.",
    },
    closing: {
      hashtag: "Canal'Art 2026 · Chapitre 01",
      title: "Le Fil commence ici.",
      body: "Suivre Castelnau d'Estrétefonds, c'est suivre la genèse du projet. Chaque événement local préfigure le grand parcours — ateliers, vernissages, rencontres avec les artistes.",
      cta: "Rejoindre Les Arteurs à Castelnau d'Estrétefonds",
    },
  },

  // ────────────────────────────────────────────────────────────────
  "toulouse": {
    heroKind: "curator",
    tagline: "La ville rose reçoit le Fil",
    hero: {
      heroLabel: "§ La curatrice invitée",
      title: "Olga Yaméogo",
      subtitle: "Peintre · Burkina Faso",
      body: [
        "Toulouse, ville de Riquet, ville rose où le Canal du Midi trouve son origine au Port de l'Embouchure, reçoit une carte blanche à Olga Yaméogo — peintre burkinabé dont l'œuvre habite les figures féminines, les mémoires africaines et la couleur.",
        "Le chapitre toulousain déploiera 29 artistes invités par Olga sur les berges du canal, du Pont-Neuf aux Ponts-Jumeaux — là où le canal latéral à la Garonne et le Canal du Midi se rejoignent. Toulouse est le nœud géographique et symbolique du parcours.",
        "La programmation valorise les liens entre l'Afrique de l'Ouest et le Sud-Ouest français : diasporas, écritures textiles, mémoires partagées. Un dialogue entre les rives d'un même Fil.",
      ],
    },
    projet: {
      title: "Chapitre 02 · Le nœud du Fil",
      lead: "Point d'origine du Canal du Midi, Toulouse est le nœud où toutes les eaux se rejoignent — celles de la Garonne, du canal, et des mémoires.",
      stats: [
        { n: "29", u: "artistes invités", d: "curation Olga Yaméogo" },
        { n: "05", u: "escales",          d: "le long du canal urbain" },
        { n: "1667", u: "chantier",       d: "début du Canal du Midi" },
      ],
      quote: "« Riquet a rêvé le canal ici, à Toulouse. C'est ici qu'on lui répond, quatre siècles plus tard. »",
      quoteAuthor: "— Les Arteurs",
    },
    worksLabel: "§ Œuvres · L'univers d'Olga Yaméogo & la ville rose",
    works: [
      { url: "https://les-arteurs.org/wp-content/uploads/2024/06/Olga-Yameogo-2.jpg", caption: "Olga Yaméogo · peinture (série féminines)" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/71txfb79_Le%20pont%20neuf%20-%20TOULOUSE_MIDI_TOULOUSAIN%20-%20Copie.jpg", caption: "Le Pont-Neuf · Toulouse · Garonne" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/166086f2_1000550422.webp", caption: "Le canal traversant Toulouse" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/vixxxr17_1000550458.webp", caption: "Berges du canal · quartiers toulousains" },
      { url: "https://commons.wikimedia.org/wiki/Special:FilePath/Toulouse_le_canal_du_Midi.jpg?width=1600", caption: "Canal du Midi bordé de platanes · Toulouse" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/yh3e8sxn_pont-deyme-vnf.jpg", caption: "Pont d'Ayme · patrimoine hydraulique VNF" },
    ],
    invitedArtists: [
      { slug: "olga-yameogo", name: "Olga Yaméogo", country: "Burkina Faso", image: "https://les-arteurs.org/wp-content/uploads/2024/06/Olga-Yameogo-2.jpg" },
    ],
    invited: {
      title: "Les 29 artistes invités",
      body: "Olga Yaméogo compose la programmation toulousaine : 29 artistes venus du monde entier, en dialogue avec les mémoires du canal et les diasporas du Sud-Ouest.",
      count_announced: 1,
      total: 29,
      note: "Les artistes seront annoncés progressivement par Olga Yaméogo. Cette page est mise à jour au fil des confirmations.",
    },
    closing: {
      hashtag: "Canal'Art 2026 · Chapitre 02",
      title: "Le nœud toulousain.",
      body: "Toulouse est le point où tout se joue : origine du canal, ville de Riquet, carrefour des mondes. Le Fil s'y noue avant de s'étendre vers la mer.",
      cta: "Découvrir Olga Yaméogo",
    },
  },

  // ────────────────────────────────────────────────────────────────
  "castelnaudary": {
    heroKind: "site",
    tagline: "Le partage des eaux",
    hero: {
      heroLabel: "§ Le lieu",
      title: "Le Grand Bassin",
      subtitle: "7 hectares · 1666-1671",
      body: [
        "Le Grand Bassin de Castelnaudary est le plus vaste plan d'eau artificiel du Canal du Midi : sept hectares, creusés par les ouvriers de Riquet pour servir de port de refuge aux barques du XVIIᵉ siècle.",
        "À proximité, le seuil de Naurouze — ligne de partage des eaux entre l'Atlantique et la Méditerranée — marque le point le plus haut du canal (189 m). Chaque goutte qui tombe ici choisit son océan.",
        "En 2026, Castelnaudary devient le chapitre du choix, du seuil, de la bifurcation. Le programme mettra à l'honneur les artistes qui travaillent la question du passage, de la ligne, du départ — et l'écluse quadruple de Saint-Roch, unique en son genre.",
      ],
    },
    projet: {
      title: "Chapitre 04 · L'eau qui choisit",
      lead: "Sur le Grand Bassin, les artistes du Fil interrogeront la ligne de partage — cette frontière invisible qui décide du destin des eaux, et des œuvres.",
      stats: [
        { n: "07", u: "hectares",            d: "plan d'eau artificiel" },
        { n: "189", u: "m d'altitude",       d: "seuil de Naurouze" },
        { n: "1671", u: "achèvement",        d: "sous la direction de Riquet" },
      ],
      quote: "« Ici, l'eau ne coule plus. Elle décide. »",
      quoteAuthor: "— Les Arteurs",
    },
    worksLabel: "§ Le lieu · Regards",
    works: [
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/sge76xu2_bassin-castelnaudary-vue-aerienne-vnf%20-%20Copie.jpg", caption: "Grand Bassin de Castelnaudary · vue aérienne" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/1hoi5vut_04_castelnaudary.webp", caption: "Écluses de Saint-Roch · quadruple écluse" },
      { url: "https://commons.wikimedia.org/wiki/Special:FilePath/Castelnaudary_bassin_canal.jpg?width=1600", caption: "Le port et le bassin, cœur historique de Castelnaudary" },
    ],
    invitedArtists: [],
    invited: {
      title: "Programme en construction",
      body: "Le chapitre castelnaudarien cherche son curateur et ses artistes. Les Arteurs invitent créatrices et créateurs à répondre à la thématique du partage des eaux — sculpture, installation, land art, performance.",
      count_announced: 0,
      total: 24,
      note: "Fondations, mécènes et institutions : ce chapitre attend son parrain. Une opportunité rare de soutenir un site UNESCO majeur.",
    },
    closing: {
      hashtag: "Canal'Art 2026 · Chapitre 04",
      title: "Le chapitre du seuil.",
      body: "Sur le plus vaste bassin du canal, l'art viendra questionner ce que signifie choisir sa direction. Un site à parrainer — l'histoire y est déjà écrite.",
      cta: "Devenir mécène de Castelnaudary",
    },
  },

  // ────────────────────────────────────────────────────────────────
  "malpas": {
    heroKind: "curator",
    tagline: "La photographie sous la montagne",
    hero: {
      heroLabel: "§ Le curateur invité",
      title: "Arno Brignon",
      subtitle: "Photographe · France",
      body: [
        "Arno Brignon est un photographe français qui travaille au long cours sur les paysages, la mémoire et les gens. Ses séries — publiées dans la presse et exposées dans les festivals de photographie documentaire — habitent les marges, les seuils, les territoires que l'on traverse sans les voir.",
        "Le tunnel du Malpas, percé en 1679-1680 sous la colline d'Ensérune, est le premier tunnel de navigation au monde. Un geste d'audace du XVIIᵉ : Riquet fit creuser 165 mètres de roche pour éviter un long détour. Au-dessus, l'oppidum d'Ensérune veille sur l'étang de Montady et ses drains rayonnants médiévaux.",
        "Arno Brignon compose ici une programmation photographique qui prend le tunnel pour métaphore : traverser ce qui semble impossible, éclairer ce qui reste dans l'ombre, faire circuler les images comme le canal fait circuler l'eau.",
      ],
    },
    projet: {
      title: "Chapitre 05 · Le passage",
      lead: "165 mètres percés sous la montagne. Le Malpas est le chapitre du geste impossible — et de son image.",
      stats: [
        { n: "165", u: "m de tunnel",          d: "sous la colline d'Ensérune" },
        { n: "1679", u: "année du chantier",   d: "premier tunnel navigable au monde" },
        { n: "29", u: "photographes invités",  d: "curation Arno Brignon" },
      ],
      quote: "« Percer la montagne pour laisser passer l'eau. Percer l'image pour laisser passer le regard. »",
      quoteAuthor: "— Les Arteurs",
    },
    worksLabel: "§ Œuvres · Le tunnel & l'univers d'Arno Brignon",
    works: [
      { url: "https://les-arteurs.org/wp-content/uploads/2024/06/Arno-Brignon-Encadrement-noir-60x90-justineetjo60x90.jpg", caption: "Arno Brignon · Justine et Jo (série)" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/wli6as6z_le-tunnel-de-malpas.webp", caption: "Tunnel du Malpas · voûte percée en 1679-1680" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/413xxa9t_le%20malpas%202.webp", caption: "Le Malpas · vue extérieure du portail sud" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/t8e3s1ci_02_le_malpas_v2.webp", caption: "Colombiers · les abords du tunnel" },
    ],
    invitedArtists: [
      { slug: "arno-brignon", name: "Arno Brignon", country: "France", image: "https://les-arteurs.org/wp-content/uploads/2024/06/Arno-Brignon-Encadrement-noir-60x90-justineetjo60x90.jpg" },
    ],
    invited: {
      title: "Les 29 photographes invités",
      body: "Arno Brignon réunit 29 photographes autour du tunnel : documentaire, plasticiens, jeunes talents, grands noms. Les images seront exposées sur les berges, autour du portail, et dans les villages d'Ensérune et Colombiers.",
      count_announced: 1,
      total: 29,
      note: "Les photographes seront annoncés au fil des confirmations. Une résidence est prévue au printemps 2026 sur le site.",
    },
    closing: {
      hashtag: "Canal'Art 2026 · Chapitre 05",
      title: "Traverser l'impossible.",
      body: "Le Malpas est le chapitre du geste audacieux. Percer, franchir, éclairer. Arno Brignon et 29 photographes y répondent à quatre siècles de distance.",
      cta: "Découvrir Arno Brignon",
    },
  },

  // ────────────────────────────────────────────────────────────────
  "loupian": {
    heroKind: "curator",
    tagline: "L'ouverture au monde · commissariat général de Canal'Art 2026",
    hero: {
      heroLabel: "§ Commissaire général",
      title: "Bertrand Ducourau",
      subtitle: "Conservateur en chef du patrimoine · Villa Loupian",
      body: [
        "Bertrand Ducourau est conservateur en chef du patrimoine et directeur du Musée de site archéologique de Loupian, qui abrite la villa gallo-romaine et ses mosaïques exceptionnelles du IVᵉ siècle. Nommé à Loupian en 2025, il porte une vision de la conservation qui dialogue avec la création contemporaine — le patrimoine comme un chantier vivant, ouvert aux artistes de notre temps.",
        "Il n'y a pas de hasard. Ce qui l'a amené dans le sud, à Toulouse en 1994, c'est précisément le chantier documentaire nécessaire au classement du Canal du Midi au patrimoine mondial de l'UNESCO — inscription obtenue deux ans plus tard, en 1996. Plus de trente ans plus tard, il est commissaire général de la première édition du FILM, pour les 30 ans de cette même inscription. Boucle rare, biographie qui rencontre l'Histoire.",
        "Pour Canal'Art 2026, il assume donc une double mission : commissaire général du parcours — coordinateur des 6 escales — et curateur de l'escale de Loupian, où il compose la programmation finale du Fil. Un choix éditorial fort : confier à celui qui a œuvré pour le classement du canal le soin de faire dialoguer 30 ans d'inscription UNESCO avec l'art d'aujourd'hui.",
        "Loupian, à l'extrémité méditerranéenne du canal, devient le chapitre de l'après. Bertrand Ducourau y interroge ce que l'art laisse derrière lui, et ce qu'il ouvre devant lui — dans un site où l'histoire se mesure en siècles, l'huître en générations, et le Fil, en horizons.",
      ],
    },
    projet: {
      title: "Chapitre 06 · Le grand large",
      lead: "Fin du canal, début de la mer. Loupian est le chapitre de l'après — de ce qui reste, de ce qui s'ouvre, de ce qui déborde. Commissariat de Bertrand Ducourau, conservateur en chef du patrimoine.",
      stats: [
        { n: "260", u: "km",                d: "depuis l'Atlantique" },
        { n: "IVᵉ", u: "siècle",             d: "villa gallo-romaine · mosaïques" },
        { n: "30", u: "artistes invités",   d: "par Bertrand Ducourau" },
      ],
      quote: "« Le canal ne s'arrête pas. Il se dilue. »",
      quoteAuthor: "— Les Arteurs",
    },
    worksLabel: "§ Simulations Canal'Art 2026 · Regards sur Loupian",
    works: [
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/b81pqkom_Gemini_Generated_Image_m0a3lxm0a3lxm0a3.png", caption: "Le Fil, installation lumineuse sur la promenade de l'étang de Thau — mâts poétiques au crépuscule", isSimulation: true },
      { url: "/belvedere-kossi-homawoo.jpg", caption: "« L'horizon n'est pas une limite… » — parchemin de Kossi Homawoo (Togo) suspendu au-dessus des reflets de l'étang", isSimulation: true },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rw29451k_LOUPIAN.webp", caption: "Loupian · lumière du soir sur l'étang" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/9he99y28_LOUPIAN2.webp", caption: "Étang de Thau · reflets et parcs conchylicoles" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/p3hkll5v_LOUPIAN3.webp", caption: "Rivages de Loupian · horizon méditerranéen" },
      { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/0el8kogd_05_loupian_v2.webp", caption: "Le village de Loupian · vue d'ensemble" },
    ],
    invitedArtists: [
      { slug: "bertrand-ducourau", name: "Bertrand Ducourau", country: "France", role: "Commissaire général · Curateur de Loupian" },
    ],
    invited: {
      title: "Les 30 artistes invités",
      body: "Bertrand Ducourau, commissaire général de Canal'Art 2026 et conservateur en chef du Musée de site de Loupian, compose la programmation méditerranéenne. Une sélection en dialogue avec les mosaïques gallo-romaines, la conchyliculture, et l'horizon du grand large.",
      count_announced: 1,
      total: 30,
      note: "La liste complète des artistes sera annoncée progressivement par le commissaire général au fil des confirmations.",
    },
    closing: {
      hashtag: "Canal'Art 2026 · Chapitre 06",
      title: "Là où le Fil se donne au large.",
      body: "Loupian conclut le parcours sous le commissariat de Bertrand Ducourau. Ce sera le lieu de la fête, du grand rassemblement final — et de la promesse d'un Fil qui continuera bien au-delà de 2026.",
      cta: "Découvrir Bertrand Ducourau",
    },
  },
};

// Announced curators for sites whose full programme isn't public yet.
const SITE_CURATORS = {
  "toulouse": {
    slug: "olga-yameogo",
    name: "Olga Yaméogo",
    country: "Burkina Faso",
    medium: "Peinture",
    image: "https://les-arteurs.org/wp-content/uploads/2024/06/Olga-Yameogo-2.jpg",
    role: "Artiste curatrice · invite 29 artistes",
    intro: "Peintre burkinabé, Olga Yaméogo compose une œuvre habitée par les figures féminines, les mémoires africaines et la couleur. Elle réunit 29 artistes autour de la ville rose.",
  },
  "malpas": {
    slug: "arno-brignon",
    name: "Arno Brignon",
    country: "France",
    medium: "Photographie",
    image: "https://les-arteurs.org/wp-content/uploads/2024/06/Arno-Brignon-Encadrement-noir-60x90-justineetjo60x90.jpg",
    role: "Photographe vedette · invite 29 artistes",
    intro: "Photographe documentaire français, Arno Brignon travaille au long cours sur les paysages, la mémoire et les gens. Il compose la programmation photographique du tunnel du Malpas et invite 29 artistes.",
  },
};

// Inscriptions poétiques par site — affichées sur les pages détail.
const SITE_POETRY = {
  "saint-ferreol": {
    title: "Le cœur du Fil",
    quote: "Bassin de retenue, source du canal. Ici bat le cœur du Fil du Midi.",
  },
  "toulouse": {
    title: "Le nœud du Fil",
    quote: "Ville rose, Garonne, Pont-Neuf — là où l'ingénieur rêva le canal.",
  },
  "castelnau": {
    title: "L'atelier du Fil",
    quote: "Foyer des Arteurs. Le Fil s'enroule ici avant de rayonner.",
  },
  "castelnaudary": {
    title: "Le partage des eaux",
    quote: "Grand Bassin, ligne de partage entre l'Atlantique et la Méditerranée. Ici, l'eau choisit son océan.",
  },
  "malpas": {
    title: "Le passage",
    quote: "Tunnel percé sous la montagne — geste d'audace du XVIIe. Le Fil traverse ce que rien ne devait traverser.",
  },
  "loupian": {
    title: "L'ouverture au monde",
    quote: "Étang, horizon, lumière. Le canal ne s'arrête pas — il se dilue dans le monde.",
  },
};

export default function SiteDetailPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [state, setState] = useState("loading");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    setState("loading");
    api.get("/sites").then((r) => {
      const s = r.data.find(x => x.slug === slug);
      if (s) { setSite(s); setState("ok"); }
      else setState("notfound");
    }).catch(() => setState("notfound"));
  }, [slug]);

  if (state === "notfound") return <Navigate to="/parcours#parcours-2026" replace />;
  if (!site) return <main className="min-h-screen flex items-center justify-center"><div className="font-mono text-[11px] tracking-[0.24em] uppercase text-fg/50">Chargement…</div></main>;

  const content = en ? mergeSiteContent(SITE_CONTENT[site.slug], SITE_CONTENT_EN[site.slug]) : SITE_CONTENT[site.slug];
  const poetry = en ? (SITE_POETRY_EN[site.slug] || SITE_POETRY[site.slug]) : SITE_POETRY[site.slug];

  return (
    <main className="pt-32 pb-20" data-testid="site-detail-page" data-slug={site.slug}>
      <PageSEO
        title={`${site.name} — Chapitre ${site.chapter}`}
        description={`${site.name} · ${site.role} · ${content?.tagline || site.description?.slice(0, 120) || "Canal'Art 2026 — 30 ans UNESCO"}`}
        image={site.image}
        imageAlt={`${site.name} — Le Fil du Midi · Canal'Art 2026`}
        path={`/sites/${site.slug}`}
        type="article"
      />
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <Link to="/parcours#parcours-2026" className="meta-label text-fg/60 link-underline"><ArrowLeft size={12} className="inline mr-1" /> {en ? "Back to the Trail" : "Retour au Parcours"}</Link>
        <div className="mt-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Chapter" : "Chapitre"} {site.chapter} · {site.km}</div>
            <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mt-6">{site.name}</h1>
            <div className="mt-4 font-mono text-[11px] tracking-[0.24em] uppercase text-fg/60">{site.role}</div>
            {content?.tagline && <div className="mt-6 font-serif italic text-2xl md:text-3xl leading-tight text-cinabre">{content.tagline}</div>}
          </div>
          <div className="md:col-span-5 flex flex-col gap-6">
            {content?.posterComposite ? (
              <div className="border border-fg/15 overflow-hidden bg-fg/[0.02]">
                <PosterComposite {...content.posterComposite} />
              </div>
            ) : (
              site.poster && <div className="border border-fg/15 overflow-hidden bg-fg/[0.02]"><img src={site.poster} alt={`Affiche ${site.name}`} className="w-full aspect-[2/3] object-cover" /></div>
            )}
          </div>
        </div>

        <div className="mt-12 border border-fg/15 overflow-hidden relative">
          <img src={site.image} alt={site.name} className="w-full aspect-[16/9] object-cover" />
          {poetry && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.65, 0, 0.35, 1] }}
              className="absolute inset-0 flex items-end p-8 md:p-16 bg-gradient-to-t from-fg/70 via-fg/20 to-transparent"
              data-testid="site-poetry"
            >
              <div className="max-w-3xl text-bg">
                <div className="font-mono text-[11px] md:text-xs tracking-[0.32em] uppercase text-cinabre drop-shadow">
                  § {site.name}
                </div>
                <div className="mt-4 font-serif italic font-light text-4xl md:text-6xl leading-[0.95] drop-shadow-lg">
                  {poetry.title}
                </div>
                <div className="mt-5 font-serif text-lg md:text-xl text-bg/90 leading-snug drop-shadow-md max-w-2xl">
                  « {poetry.quote} »
                </div>
              </div>
            </motion.div>
          )}
        </div>
        <p className="mt-8 max-w-3xl text-fg/85 leading-relaxed text-lg md:text-xl">{site.description}</p>

        <div className="mt-10">
          <SocialShare
            title={`${site.name} · Le Fil du Midi`}
            text={`${site.name} — ${content?.tagline || site.role} · Canal'Art 2026`}
            shareUrl={`${window.location.origin}/api/og/sites/${site.slug}`}
          />
        </div>
      </section>

      {content ? (
        <>
          {/* Artist bio */}
          <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="meta-label text-cinabre">{content.hero.heroLabel || (en ? "§ The invited artist" : "§ L'artiste invité")}</div>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mt-4">{content.hero.title}</h2>
              <div className="mt-3 font-mono text-[11px] tracking-[0.24em] uppercase text-fg/60">{content.hero.subtitle}</div>
            </div>
            <div className="md:col-span-8 space-y-5 text-fg/85 text-lg leading-relaxed">
              {content.hero.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </section>

          {/* Projet */}
          <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="site-projet">
            <div className="meta-label text-cinabre">§ {en ? "The project" : "Le projet"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4 max-w-4xl">{content.projet.title}</h2>
            <p className="mt-8 max-w-3xl text-fg/85 leading-relaxed text-lg md:text-xl">{content.projet.lead}</p>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-fg/15 border border-fg/15">
              {content.projet.stats.map((s, i) => (
                <div key={i} className="bg-bg p-8">
                  <div className="font-serif text-6xl md:text-7xl leading-none text-cinabre">{s.n}</div>
                  <div className="mt-4 font-serif text-2xl leading-tight">{s.u}</div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{s.d}</div>
                </div>
              ))}
            </div>

            <blockquote className="mt-14 border-l-2 border-cinabre pl-8 py-2 max-w-3xl">
              <p className="font-serif italic text-2xl md:text-3xl leading-tight text-fg/90">{content.projet.quote}</p>
              <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{content.projet.quoteAuthor}</div>
            </blockquote>
          </section>

          {slug === "saint-ferreol" && <BatlleTimeline />}

          {/* Works gallery */}
          <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
              <div className="meta-label text-cinabre">{content.worksLabel || `§ Œuvres · L'univers de ${content.hero.title}`}</div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">Cliquer pour agrandir</div>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {content.works.map((w, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.05 }}
                  className="border border-fg/15 overflow-hidden group cursor-zoom-in relative"
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`work-${i}`}
                >
                  <img src={w.url} alt={w.caption} className="w-full aspect-[4/5] object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]" loading="lazy" />
                  {w.isSimulation && (
                    <div className="absolute top-3 left-3 bg-cinabre text-bg font-mono text-[9px] tracking-[0.28em] uppercase px-2.5 py-1.5 shadow-lg" data-testid={`work-${i}-simulation-badge`}>
                      Simulation · Canal'Art 2026
                    </div>
                  )}
                  <div className="absolute top-3 right-3 w-9 h-9 bg-fg/70 text-bg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand size={14} strokeWidth={1.4} />
                  </div>
                  <figcaption className="p-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{w.caption}</figcaption>
                </motion.figure>
              ))}
            </div>
          </section>

          {/* Invited artists — placeholders */}
          <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto">
            <div className="meta-label text-cinabre">§ {en ? "Carte blanche" : "Carte blanche"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4">{content.invited.title}</h2>
            <p className="mt-6 max-w-2xl text-fg/80 leading-relaxed text-lg">{content.invited.body}</p>
            <div className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
              <span className="text-cinabre">{content.invited.count_announced} {en ? `artist${content.invited.count_announced !== 1 ? "s" : ""} announced` : `artiste${content.invited.count_announced !== 1 ? "s" : ""} annoncé${content.invited.count_announced !== 1 ? "s" : ""}`}</span> · {content.invited.total} {en ? "in total" : "au total"}
            </div>

            <div className="mt-10 grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {Array.from({ length: content.invited.total }).map((_, i) => {
                const announced = content.invitedArtists?.[i];
                if (announced && announced.image) {
                  return (
                    <Link
                      key={i}
                      to={`/artistes/${announced.slug}`}
                      className="group relative border border-fg/25 aspect-square overflow-hidden hover:border-cinabre transition-colors"
                      data-testid={`invited-artist-${announced.slug}`}
                    >
                      <img
                        src={announced.image}
                        alt={announced.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-fg/45 group-hover:bg-fg/20 transition-colors duration-500" />
                      <div className="absolute inset-0 p-2 flex flex-col justify-between text-bg">
                        <div className="font-mono text-[9px] tracking-[0.24em] uppercase opacity-80">§ {String(i + 1).padStart(2, "0")}</div>
                        <div>
                          <div className="font-serif text-sm md:text-base leading-tight">{announced.name}</div>
                          <div className="font-mono text-[8px] tracking-[0.24em] uppercase opacity-80 mt-1">{announced.country}</div>
                        </div>
                      </div>
                    </Link>
                  );
                }
                if (announced) {
                  const Tag = announced.url ? "a" : "div";
                  const extra = announced.url ? { href: announced.url, target: "_blank", rel: "noreferrer" } : {};
                  return (
                    <Tag
                      key={i}
                      {...extra}
                      className="group relative border border-fg/25 aspect-square overflow-hidden bg-fg/[0.03] hover:border-cinabre transition-colors"
                      data-testid={`invited-artist-named-${i}`}
                    >
                      <div className="absolute inset-0 p-2 flex flex-col justify-between">
                        <div className="flex items-start justify-between">
                          <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-fg/50">§ {String(i + 1).padStart(2, "0")}</div>
                          {announced.url && <ExternalLink size={11} strokeWidth={1.4} className="text-fg/40 group-hover:text-cinabre transition-colors" />}
                        </div>
                        <div>
                          <div className="font-serif text-sm md:text-base leading-tight group-hover:text-cinabre transition-colors">{announced.name}</div>
                          {announced.sub && <div className="font-mono text-[8px] tracking-[0.18em] uppercase text-fg/55 mt-1 leading-snug">{announced.sub}</div>}
                        </div>
                      </div>
                    </Tag>
                  );
                }
                return (
                  <div key={i} className="border border-dashed border-fg/25 aspect-square flex flex-col items-center justify-center text-fg/50 hover:border-cinabre hover:text-cinabre transition-colors">
                    <div className="font-serif text-2xl leading-none">{i + 1}</div>
                    <div className="mt-2 font-mono text-[9px] tracking-[0.24em] uppercase">À annoncer</div>
                  </div>
                );
              })}
            </div>
            <p className="mt-8 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 max-w-2xl">{content.invited.note}</p>
          </section>

          {/* Closing */}
          <section className="mt-24 md:mt-32 px-6 md:px-12">
            <div className="max-w-[1200px] mx-auto bg-fg text-bg p-10 md:p-20 border border-fg">
              <div className="meta-label text-cinabre">§ {content.closing.hashtag}</div>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight mt-6 max-w-3xl">{content.closing.title}</h2>
              <p className="mt-8 text-bg/85 max-w-2xl leading-relaxed text-lg">{content.closing.body}</p>
              <Link to={content.heroKind === "site" ? "/mecenat" : "/rejoindre"} className="mt-10 inline-flex items-center gap-2 btn-editorial bg-bg text-fg border-bg hover:bg-cinabre hover:text-bg hover:border-cinabre">
                {content.closing.cta} <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Curator preview (if a curator has been announced) */}
          {SITE_CURATORS[site.slug] && (() => {
            const c = en ? { ...SITE_CURATORS[site.slug], ...(SITE_CURATORS_EN[site.slug] || {}) } : SITE_CURATORS[site.slug];
            return (
              <section className="mt-16 md:mt-24 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="curator-preview">
                <div className="meta-label text-cinabre">§ Le curateur invité</div>
                <div className="mt-8 grid md:grid-cols-12 gap-10 items-start">
                  <Link to={`/artistes/${c.slug}`} className="md:col-span-5 group block border border-fg/15 overflow-hidden" data-testid={`curator-link-${c.slug}`}>
                    <img src={c.image} alt={c.name} className="w-full aspect-[4/5] object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]" loading="lazy" />
                  </Link>
                  <div className="md:col-span-7">
                    <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-fg/60">{c.country} · {c.medium}</div>
                    <h2 className="mt-3 font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight">{c.name}</h2>
                    <div className="mt-3 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre">{c.role}</div>
                    <p className="mt-6 text-fg/85 leading-relaxed text-lg max-w-xl">{c.intro}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to={`/artistes/${c.slug}`} className="btn-editorial" data-testid="curator-cta">
                        Découvrir la fiche artiste <ArrowRight size={14} />
                      </Link>
                    </div>

                    {/* Artist grid placeholder */}
                    <div className="mt-12">
                      <div className="flex items-baseline justify-between flex-wrap gap-3">
                        <div className="meta-label text-cinabre">§ Les 29 artistes invités</div>
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">0 / 29 annoncés</div>
                      </div>
                      <div className="mt-4 grid grid-cols-5 sm:grid-cols-7 md:grid-cols-8 gap-2">
                        {Array.from({ length: 29 }).map((_, i) => (
                          <div key={i} className="border border-dashed border-fg/25 aspect-square flex items-center justify-center text-fg/40 hover:border-cinabre hover:text-cinabre transition-colors">
                            <div className="font-serif text-lg leading-none">{i + 1}</div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                        Les artistes seront annoncés progressivement par {c.name}.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            );
          })()}

          {/* Mécénat CTA */}
          <section className="mt-24 md:mt-32 px-6 md:px-12" data-testid="mecenat-cta">
            <div className="max-w-[1200px] mx-auto bg-fg text-bg p-10 md:p-16 border border-fg">
              <div className="meta-label text-cinabre">§ Parrainer ce site</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[0.95] tracking-tight mt-6 max-w-3xl">
                Devenez le mécène de {site.name}.
              </h2>
              <p className="mt-6 text-bg/85 max-w-2xl leading-relaxed text-lg">
                Le Fil du Midi cherche des <span className="text-cinabre">fondations, mécènes et institutions</span> pour financer les 6 sites d'exposition. Michel Batlle auto-finance uniquement ses propres sculptures pour EXPLOSITION à Saint-Ferréol ; tout le reste — production des 194 artistes invités, scénographie, transport, communication — reste à financer avec vous. Nous avons l'appui technique et de communication de VNF, des communes traversées et de l'UNESCO.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/mecenat" className="btn-editorial bg-bg text-fg border-bg hover:bg-cinabre hover:text-bg hover:border-cinabre" data-testid="mecenat-link">
                  Découvrir le mécénat <ArrowRight size={14} />
                </Link>
                <Link to="/rejoindre" className="btn-editorial border-bg/40 text-bg hover:bg-cinabre hover:border-cinabre">
                  Nous contacter
                </Link>
              </div>
            </div>
          </section>

          {!SITE_CURATORS[site.slug] && (
            <section className="mt-16 md:mt-24 px-6 md:px-12 max-w-[1200px] mx-auto">
              <div className="border border-fg/15 p-10 md:p-16 text-center">
                <div className="meta-label text-cinabre">§ Programme en préparation</div>
                <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight tracking-tight">Le curateur de ce site sera dévoilé prochainement.</h2>
                <p className="mt-6 font-serif italic text-xl text-fg/70 max-w-2xl mx-auto">
                  Cette page sera mise à jour au fil des confirmations. L'exposition est en construction : votre soutien peut en accélérer la réalisation.
                </p>
              </div>
            </section>
          )}
        </>
      )}

      {content?.works && (
        <Lightbox
          images={content.works}
          startIndex={Math.max(0, lightboxIndex)}
          open={lightboxIndex >= 0}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
    </main>
  );
}
